import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  BookOpen,
  Bookmark,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  GraduationCap,
  LayoutGrid,
  Library,
  LineChart,
  MessageCircle,
  Moon,
  Sun,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
  FileText,
  RotateCcw,
  Download,
  AlertCircle,
  LockKeyhole,
  ClipboardCheck,
  Play,
  Flag,
} from "lucide-react";
import "./learning.css";
import "./workspace.css";

const LearningContext = createContext(null);
const useLearning = () => useContext(LearningContext);
const errorText = e => typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Belum berhasil terhubung. Coba lagi; jawabanmu tetap ada di halaman ini.";
const endpoint = role => `/ruang-belajar/roles/${role}`;

export default function LearningWorkspace({ payload, account, role, onRole, brand, onBusy }) {
  const value = {
    ...payload, account, role, onRole, brand,
    decks: [payload.deck, ...(payload.references.length ? [{id: "frontliner", role: "Frontliner", file: "Panduan Frontliner", lessons: payload.references}] : [])],
    documents: payload.documents,
    getLesson: id => [...payload.deck.lessons, ...payload.references].find(l => l.id === id),
    questionsFor: () => payload.topics,
    reviewNotes: payload.review_notes,
    practicalChecks: payload.practical_checks,
    VERSION: payload.version,
  };
  return <LearningContext.Provider value={value}><Academy onBusy={onBusy} /></LearningContext.Provider>;
}
const icons = [ShieldCheck, MessageCircle, ClipboardCheck, Target];
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);
const score = (record) =>
  record
    ? pct(record.answers.filter((a) => a.correct).length, record.answers.length)
    : null;
const dateLabel = (iso) =>
  new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Jakarta",
  }).format(new Date(iso));
function Dialog({ title, onClose, children, wide = false }) {
  const ref = useRef(null),
    id = useId();
  useEffect(() => {
    const d = ref.current;
    d.showModal();
    return () => d.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`a-dialog ${wide ? "wide" : ""}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog-head">
        <div>
          <span className="eyebrow">RUANG BELAJAR</span>
          <h2 id={id}>{title}</h2>
        </div>
        <button
          className="icon-btn"
          onClick={onClose}
          aria-label="Tutup dialog"
        >
          <X />
        </button>
      </div>
      {children}
    </dialog>
  );
}
function Progress({ value, label }) {
  return (
    <div
      className="progress-track"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
    >
      <span style={{ width: `${value}%` }} />
    </div>
  );
}
function Inline({ text }) {
  return String(text)
    .split(/\*\*(.*?)\*\*/g)
    .map((s, i) => (i % 2 ? <strong key={i}>{s}</strong> : s));
}
function Markdown({ text }) {
  const result = [],
    lines = text.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i].trim();
    if (!l || /^[-_]{3,}$/.test(l)) continue;
    if (l.startsWith("```")) {
      const block = [];
      while (++i < lines.length && !lines[i].startsWith("```"))
        block.push(lines[i]);
      result.push(<pre key={i}>{block.join("\n")}</pre>);
      continue;
    }
    if (l.startsWith("|")) {
      const rows = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        const cells = lines[i]
          .trim()
          .split("|")
          .slice(1, -1)
          .map((s) => s.trim());
        if (!cells.every((s) => /^[-:\s]+$/.test(s))) rows.push(cells);
        i++;
      }
      i--;
      result.push(
        <div className="table-scroll" tabIndex={0} key={i}>
          <table>
            <tbody>
              {rows.map((r, j) => (
                <tr key={j}>
                  {r.map((c, k) =>
                    j === 0 ? (
                      <th key={k}>
                        <Inline text={c} />
                      </th>
                    ) : (
                      <td key={k}>
                        <Inline text={c} />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^#{1,6} /.test(l)) {
      result.push(<h3 key={i}>{l.replace(/^#+ /, "")}</h3>);
      continue;
    }
    result.push(
      <p key={i} className={l.startsWith(">") ? "source-quote" : ""}>
        <Inline text={l.replace(/^>\s?/, "")} />
      </p>,
    );
  }
  return <div className="source-document">{result}</div>;
}
function SlideContent({ lesson, onOpen }) {
  const {decks} = useLearning();
  const [flipped, setFlipped] = useState({});
  if (lesson.held) return <div className="review-intro"><LockKeyhole/><div><h3>Menunggu review pengelola</h3><p>{lesson.subtitle}</p></div></div>;
  if (lesson.number === 2) {
    const sourceDeck = decks.find((d) => d.id === lesson.id.split(":")[0]);
    return (
      <div className="lesson-toc">
        {sourceDeck.lessons.filter((item) => item.number > 2).map((item) => (
          <button key={item.id} onClick={() => onOpen(item.id)}>
            <span>{String(item.number - 2).padStart(2, "0")}</span>
            <b>{item.title}</b>
            <ArrowRight size={16} />
          </button>
        ))}
      </div>
    );
  }
  if (lesson.title === "Nilai JUJUR")
    return (
      <>
        <p className="reader-lead">
          Sentuh setiap kartu untuk membuka maknanya.
        </p>
        <div className="values-grid">
          {[
            [
              "J",
              "Jemput bola",
              "Pelanggan disapa duluan, tidak menunggu ditanya.",
            ],
            [
              "U",
              "Utuh",
              "Selisih kas atau stok dilaporkan apa adanya, sekecil apa pun.",
            ],
            [
              "J",
              "Jelas",
              "Harga, kondisi, dan garansi dijelaskan sebelum transaksi.",
            ],
            [
              "U",
              "Urus sampai tuntas",
              "Komplain didampingi sampai selesai, bukan dilempar.",
            ],
            ["R", "Rapi", "Meja, display, data, dan laporan tertib."],
          ].map(([letter, title, text], i) => (
            <button
              key={i}
              className={`value-card ${flipped[i] ? "flipped" : ""}`}
              onClick={() => setFlipped((s) => ({ ...s, [i]: !s[i] }))}
              aria-pressed={!!flipped[i]}
            >
              <span>{letter}</span>
              <b>{title}</b>
              <p>{flipped[i] ? text : "Ketuk untuk membuka"}</p>
            </button>
          ))}
        </div>
        <aside className="reader-tip">
          <ShieldCheck />
          Menutupi selisih lebih berat daripada membuat selisih.
        </aside>
      </>
    );
  const groups = [];
  for (const block of lesson.blocks) {
    const marker =
      block.length === 1 &&
      /^(\d+|[A-E]|✕|★|BOLEH|TIDAK PERNAH BOLEH|UCAPKAN|JANGAN UCAPKAN)$/.test(
        block[0],
      );
    if (marker) groups.push({ marker: block[0], blocks: [] });
    else {
      if (!groups.length) groups.push({ blocks: [] });
      groups[groups.length - 1].blocks.push(block);
    }
  }
  return (
    <div className="lesson-blocks">
      {groups.map((g, i) => (
        <section
          className={g.marker ? "lesson-step" : "lesson-paragraph"}
          key={i}
        >
          {g.marker && (
            <span className="step-mark">
              {g.marker === "✕" ? <X size={16} /> : g.marker}
            </span>
          )}
          <div>
            {g.blocks.map((b, j) =>
              j === 0 && g.marker ? (
                <h3 key={j}>{b.join(" ")}</h3>
              ) : (
                <p key={j}>
                  {b.map((t, k) => (
                    <React.Fragment key={k}>
                      {k > 0 && <br />}
                      {t}
                    </React.Fragment>
                  ))}
                </p>
              ),
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
function Assessment({ mode, role, onDone, onExit }) {
  const {VERSION} = useLearning();
  const [questions, setQuestions] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const [index, setIndex] = useState(0), [answers, setAnswers] = useState({}), [confidence, setConfidence] = useState({}), [confirm, setConfirm] = useState(false);
  const attemptId = useRef(crypto.randomUUID());
  const loadQuestions = () => { setError(""); api.get(`${endpoint(role)}/assessment/${mode}`).then(({data}) => setQuestions(data.questions)).catch(e => setError(errorText(e))); };
  useEffect(() => { loadQuestions(); /* fixed role/mode via component key */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const submit = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try {
      const {data} = await api.post(`${endpoint(role)}/assessment/${mode}`, {version: VERSION, attempt_id: attemptId.current,
        answers: questions.map(q => ({id:q.id, chosen:answers[q.id], confidence:confidence[q.id] || "belum-yakin"}))});
      onDone(data);
    } catch (e) { setError(errorText(e)); setBusy(false); }
  };
  if (!questions) return <section className="panel"><p role="status">{error || "Menyiapkan pertanyaan…"}</p>{error && <button className="primary" onClick={loadQuestions}>Coba lagi</button>}<button className="text-btn" onClick={onExit}>Kembali</button></section>;
  const q = questions[index], chosen = answers[q.id], order = mode === "pre" ? [0,1,2] : [2,0,1];
  return (
    <section className="assessment" aria-busy={busy}><fieldset disabled={busy} style={{border:0, padding:0, minWidth:0}}>
      <button className="text-btn" onClick={onExit}>
        <ArrowLeft size={17} />
        Kembali ke ruang belajar
      </button>
      <div className="assessment-meta">
        <span className="badge blue">
          {mode === "pre" ? "TES AWAL" : "TES AKHIR"}
        </span>
        <span>
          {index + 1} dari {questions.length} pertanyaan
        </span>
      </div>
      <Progress
        value={pct(index, questions.length)}
        label="Progres pertanyaan"
      />
      <div className="question-card">
        <span className="eyebrow">{q.topic}</span>
        <h1>{q.prompt}</h1>
        <p>
          {mode === "pre"
            ? "Jawab sesuai pengetahuanmu sekarang. Tidak tahu juga jawaban yang berguna."
            : "Terapkan yang sudah kamu pelajari pada situasi berikut."}
        </p>
        <div
          role="radiogroup"
          aria-label="Pilihan jawaban"
          className="answer-options"
        >
          {[...order, -1].map((n, i) => (
            <button
              key={n}
              role="radio"
              aria-checked={chosen === n}
              onClick={() => {
                setAnswers((s) => ({ ...s, [q.id]: n }));
                setConfirm(false);
              }}
              className={chosen === n ? "selected" : ""}
            >
              <span className="option-letter">
                {n === -1 ? "?" : String.fromCharCode(65 + i)}
              </span>
              <span>{n === -1 ? "Saya belum tahu" : q.options[n]}</span>
              {chosen === n && <Check size={19} />}
            </button>
          ))}
        </div>
        <div className="confidence">
          <span>Seberapa yakin kamu?</span>
          <div>
            {[
              ["yakin", "Yakin"],
              ["ragu", "Masih ragu"],
            ].map(([v, l]) => (
              <button
                key={v}
                className={confidence[q.id] === v ? "selected" : ""}
                aria-pressed={confidence[q.id] === v}
                onClick={() => setConfidence((s) => ({ ...s, [q.id]: v }))}
              >
                {l}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="assessment-footer">
        <button
          className="secondary"
          disabled={!index}
          onClick={() => {
            setIndex((i) => i - 1);
            setConfirm(false);
          }}
        >
          <ArrowLeft size={17} />
          Sebelumnya
        </button>
        {index < questions.length - 1 ? (
          <button
            className="primary"
            disabled={chosen === undefined}
            onClick={() => setIndex((i) => i + 1)}
          >
            Selanjutnya
            <ArrowRight size={17} />
          </button>
        ) : (
          <button
            className="primary"
            disabled={chosen === undefined}
            onClick={() => setConfirm(true)}
          >
            Tinjau & kirim
            <CheckCircle2 size={17} />
          </button>
        )}
      </div>
      {confirm && (
        <div className="submission">
          <b>{questions.length} jawaban siap dikirim.</b>
          <p>
            {mode === "pre"
              ? "Hasil pertama ini akan menjadi pembanding tetap untuk tes akhirmu."
              : "Hasil ini menjadi percobaan baru. Hasil tes awal tetap tersimpan."}
          </p>
          <button className="primary" onClick={submit}>
            Simpan hasil {mode === "pre" ? "tes awal" : "tes akhir"}
          </button>
        </div>
      )}
      <p className="quiet center">
        <LockKeyhole size={14} />
        Hasil tersimpan di akunmu dan dapat ditinjau pengelola pelatihan.
      </p>{error && <p className="warning" role="alert">{error}</p>}{busy && <p role="status">Menyimpan hasil…</p>}</fieldset>
    </section>
  );
}
function Practice({ kind, role, onClose, onSave }) {
  const steps = [
    "Sapa",
    "Gali kebutuhan",
    "Tawarkan unit",
    "Tawarkan aksesoris",
    "Arahkan ke kasir",
  ];
  const [order, setOrder] = useState([2, 0, 4, 1, 3]),
    [selected, setSelected] = useState(null),
    [feedback, setFeedback] = useState(false);
  const {VERSION, questionsFor, decks} = useLearning();
  const [question, setQuestion] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const correct = feedback?.correct;
  const loadQuestion = () => { setError(""); api.get(`${endpoint(role)}/assessment/practice`).then(({data}) => setQuestion({...data.questions[0], ref: questionsFor(role)[3].ref})).catch(e=>setError(errorText(e))); };
  useEffect(() => { if(kind !== "order") loadQuestion(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const check = async () => {
    if (busy) return;
    if (correct) { onClose(); return; }
    setBusy(true); setError("");
    try { const {data} = await api.post(`${endpoint(role)}/practice`, {version:VERSION, kind, ...(kind === "order" ? {order} : {chosen:selected})}); setFeedback(data); onSave(data.record); }
    catch(e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const move = (i, d) =>
    setOrder((s) => {
      const a = [...s];
      [a[i], a[i + d]] = [a[i + d], a[i]];
      return a;
    });
  return (
    <Dialog
      title={
        kind === "order" ? "Susun alur transaksi" : "Latihan situasi kerja"
      }
      onClose={() => !busy && onClose()}
    >
      <div className="practice-body"><fieldset disabled={busy} style={{border:0,padding:0,minWidth:0}}>
      {error && <p role="alert" className="warning">{error}</p>}
        <span className="badge lilac">LATIHAN · BOLEH MENCOBA LAGI</span>
        {kind === "order" ? (
          <>
            <h3>Mana yang dilakukan lebih dulu?</h3>
            <p>
              Susun lima langkah pelayanan sesuai panduan. Gunakan tombol naik
              atau turun.
            </p>
            <div className="ordering">
              {order.map((n, i) => (
                <div key={n}>
                  <span className="order-number">{i + 1}</span>
                  <b>{steps[n]}</b>
                  <button
                    className="icon-btn"
                    disabled={i === 0}
                    aria-label={`Naikkan ${steps[n]}`}
                    onClick={() => {
                      move(i, -1);
                      setFeedback(false);
                    }}
                  >
                    <ArrowUp size={17} />
                  </button>
                  <button
                    className="icon-btn"
                    disabled={i === 4}
                    aria-label={`Turunkan ${steps[n]}`}
                    onClick={() => {
                      move(i, 1);
                      setFeedback(false);
                    }}
                  >
                    <ArrowDown size={17} />
                  </button>
                </div>
              ))}
            </div>
            <p className="source-line">Sumber: Panduan Frontliner · slide 7</p>
          </>
        ) : !question ? <p role="status">{error ? <button onClick={loadQuestion}>Coba lagi</button> : "Menyiapkan latihan…"}</p> : (
          <>
            <div className="scenario">
              <div className="scenario-avatar">
                <MessageCircle />
              </div>
              <div>
                <b>Situasi di shift kamu</b>
                <p>{question.prompt}</p>
              </div>
            </div>
            <div className="answer-options">
              {question.options.map((option, i) => (
                <button
                  key={i}
                  className={selected === i ? "selected" : ""}
                  onClick={() => {
                    setSelected(i);
                    setFeedback(false);
                  }}
                >
                  <span className="option-letter">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {option}
                </button>
              ))}
            </div>
            <p className="source-line">
              Sumber:{" "}
              {decks.find((d) => d.id === question.ref.split(":")[0]).role} ·
              slide {question.ref.split(":")[1]}
            </p>
          </>
        )}
        {feedback && (
          <div
            role="status"
            className={`feedback ${correct ? "success" : "retry"}`}
          >
            <b>
              {correct
                ? "Tepat. Kamu menangkap alurnya."
                : "Belum tepat. Coba pikirkan lagi."}
            </b>
            <p>
              {feedback.explanation}
            </p>
          </div>
        )}
        <button
          className="primary full"
          disabled={kind !== "order" && selected === null}
          onClick={check}
        >
          {busy ? "Menyimpan…" : feedback && correct ? "Selesai" : "Periksa jawabanku"}
          <ArrowRight size={17} />
        </button>
      </fieldset></div>
    </Dialog>
  );
}

function Academy({onBusy}) {
  const {decks, documents, getLesson, questionsFor, reviewNotes, practicalChecks, VERSION, account, role, onRole, brand, ...payload} = useLearning();
  const {theme} = useTheme();
  const [page, setPage] = useState("home");
  const [record, setRecord] = useState(payload.record), [saving, setSaving] = useState(false), [saveError, setSaveError] = useState("");
  const lock = useRef(false);
  const [draftNote, setDraftNote] = useState("");
  const [task, setTask] = useState(null),
    [lessonId, setLessonId] = useState(null),
    [documentId, setDocumentId] = useState(null),
    [practice, setPractice] = useState(null),
    [query, setQuery] = useState(""),
    [libraryTab, setLibraryTab] = useState("slides"),
    [visibleCount, setVisibleCount] = useState(12),
    [notice, setNotice] = useState(""),
    [result, setResult] = useState(null);
  const reduce = useReducedMotion(),
    deck = decks.find((d) => d.id === role),
    lesson = lessonId ? getLesson(lessonId) : null,
    doc = documents.find((d) => d.id === documentId);
  const lessons = deck.lessons.filter((l) => l.number > 2 && !l.held),
    read = lessons.filter((l) => record.read.includes(l.id)).length,
    total = lessons.length,
    progress = pct(read, total),
    last = record.attempts.at(-1),
    baselineScore = score(record.baseline),
    lastScore = score(last);
  const patchLesson = async patch => {
    if(lock.current) return false;
    lock.current = true; setSaving(true); setSaveError("");
    try { const {data} = await api.patch(`${endpoint(role)}/lesson`, {version:VERSION,...patch}); setRecord(data.record); return true; }
    catch(e) { setSaveError(errorText(e)); return false; } finally {lock.current = false; setSaving(false);}
  };
  const dirty = !!lessonId && draftNote !== (record.notes[lessonId] || "");
  useEffect(() => { onBusy(!!task || saving || dirty); return () => onBusy(false); }, [task, saving, dirty, onBusy]);
  useEffect(() => { const warn = e => { if(task || dirty) {e.preventDefault(); e.returnValue = "";} }; window.addEventListener("beforeunload",warn); return()=>window.removeEventListener("beforeunload",warn); },[task,dirty]);
  useEffect(() => { setDraftNote(record.notes[lessonId] || ""); // only a new lesson resets the draft
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[lessonId]);
  const openLesson = id => { if(saving || (dirty && !window.confirm("Catatan belum disimpan. Tinggalkan perubahan?"))) return; setLessonId(id); setSaveError(""); };
  useEffect(() => {
    setVisibleCount(12);
  }, [query, libraryTab, role]);
  useEffect(() => {
    document.querySelector(".a-dialog .reader-body")?.scrollTo(0, 0);
  }, [lessonId]);
  const navigate = (p) => {
    if (
      task &&
      !window.confirm(
        "Keluar dari tes? Jawaban yang belum dikirim belum tersimpan.",
      )
    )
      return;
    setTask(null);
    setResult(null);
    setPage(p);
    setNotice("");
    window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" });
  };
  const start = (mode) => {
    setResult(null);
    setTask(mode);
    window.scrollTo({ top: 0, behavior: reduce ? "instant" : "smooth" });
  };
  const finish = (data) => {
    setRecord(data.record); setResult({mode:task, data:data.attempt}); setTask(null); setPage("progress");
  };
  const toggleBookmark = id => patchLesson({lesson_id:id, bookmarked:!record.bookmarks.includes(id)});
  const markRead = id => patchLesson({lesson_id:id, read:true});
  const openModule = (i) => {
    setLessonId(
      deck.lessons.find(
        (l) =>
          deck.modules[i].slides.includes(l.number) &&
          !l.held && !record.read.includes(l.id),
      )?.id || deck.lessons.find(l => deck.modules[i].slides.includes(l.number) && !l.held)?.id || null,
    );
  };
  const weak =
      (last || record.baseline)?.answers.filter(
        (a) => !a.correct || a.confidence !== "yakin",
      ) || [],
    recommend =
      weak[0]?.ref ||
      lessons.find((l) => !record.read.includes(l.id))?.id ||
      lessons[0].id;
  const nav = [
    ["home", LayoutGrid, "Belajarku"],
    ["library", Library, "Pustaka SOP"],
    ["progress", LineChart, "Progres"],
    ...(account.can_manage ? [["review", Flag, "Catatan materi"]] : []),
  ];
  const download = () => {
    const blob = new Blob(
        [
          JSON.stringify(
            { name: account.name, role: deck.role, version: VERSION, ...record },
            null,
            2,
          ),
        ],
        { type: "application/json" },
      ),
      url = URL.createObjectURL(blob),
      a = document.createElement("a");
    a.href = url;
    a.download = `Hasil-belajar-${role}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <div className="learning-workspace" data-theme={theme} data-testid="learning-workspace">
      <div className="learning-toolbar">
        <div><span className="eyebrow">{brand} · RUANG BELAJAR</span><strong>Halo, {account.name || "teman belajar"}</strong></div>
        <label>Jalur belajar <select aria-label="Jalur belajar" value={role} disabled={!!task || saving || dirty} onChange={e=>onRole(e.target.value)}>{account.roles.map(d=><option value={d.id} key={d.id}>{d.role}</option>)}</select></label>
      </div>
      <nav className="learning-nav" aria-label="Navigasi Ruang Belajar">{nav.map(([id,Icon,label])=><button key={id} className={page===id&&!task ? "active" : ""} onClick={()=>navigate(id)}><Icon size={18}/>{label}</button>)}</nav>
      <div className="learning-main">
          {saveError && <p role="alert" className="warning">{saveError}</p>}
          {notice && (
            <p className="notice" role="status">
              {notice}
            </p>
          )}
          <AnimatePresence mode="wait">
            <motion.div
              key={task || page}
              initial={{ opacity: 0, y: reduce ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.2 }}
            >
              {task ? (
                <Assessment
                  key={`${role}-${task}`}
                  mode={task}
                  role={role}
                  onDone={finish}
                  onExit={() => navigate("home")}
                />
              ) : page === "home" ? (
                <>
                  <div className="page-intro">
                    <div>
                      <span className="eyebrow">
                        BELAJAR UNTUK SHIFT YANG LEBIH BAIK
                      </span>
                      <h1>
                        Ruang untuk berkembang
                        <span className="title-dot">.</span>
                      </h1>
                      <p>
                        Kenali SOP, coba situasinya, lalu praktikkan bersama
                        mentor.
                      </p>
                    </div>
                    <span className="count-label">
                      <BookOpen size={16} />
                      {deck.lessons.length} slide panduan {deck.role}
                    </span>
                  </div>
                  <div className="hero-grid">
                    <section className="hero">
                      <div className="hero-copy">
                        <span className="hero-tag">
                          <Sparkles size={14} />
                          {record.baseline
                            ? "LANGKAH KECIL, PROGRES NYATA"
                            : "MULAI DARI YANG KAMU TAHU"}
                        </span>
                        <h2>
                          {record.baseline ? (
                            <>
                              Lanjutkan belajarmu.
                              <br />
                              Kamu sudah mulai.
                            </>
                          ) : (
                            <>
                              Sudah sejauh mana
                              <br />
                              kamu mengenal SOP?
                            </>
                          )}
                        </h2>
                        <p>
                          {record.baseline
                            ? "Buka materi rekomendasi dan latih keputusanmu sebelum shift berikutnya."
                            : "Enam pertanyaan ringan untuk menemukan materi yang paling kamu butuhkan. Tidak perlu belajar dulu."}
                        </p>
                        <button
                          className="hero-button"
                          onClick={() =>
                            record.baseline
                              ? setLessonId(recommend)
                              : start("pre")
                          }
                        >
                          {record.baseline
                            ? "Buka materi rekomendasi"
                            : "Cek pengetahuan awal"}
                          <ArrowRight size={18} />
                        </button>
                        <span className="hero-meta">
                          <Clock3 size={14} />
                          {record.baseline
                            ? "Belajar sesuai ritmemu"
                            : "±3 menit · tanpa batas waktu"}
                        </span>
                      </div>
                      <div className="hero-art" aria-hidden="true">
                        <div className="orbit one" />
                        <div className="orbit two" />
                        <div className="art-spark s1">✦</div>
                        <div className="art-spark s2">✦</div>
                        <div className="floating-note note-back">
                          <ShieldCheck />
                          <span>Jaga kepercayaan</span>
                        </div>
                        <div className="floating-note note-front">
                          <div className="mini-book">
                            <BookOpen size={30} />
                          </div>
                          <small>SETIAP HARI, SEDIKIT LEBIH PAHAM</small>
                          <b>
                            Siap untuk
                            <br />
                            langkah berikutnya.
                          </b>
                          <div className="tiny-progress">
                            <i />
                            <i />
                            <i />
                            <i />
                          </div>
                        </div>
                        <div className="art-check">
                          <Check size={25} />
                        </div>
                      </div>
                    </section>
                    <section className="progress-card">
                      <div className="row-between">
                        <h3>Perjalananmu</h3>
                        <span className="icon-tile pale">
                          <Target size={19} />
                        </span>
                      </div>
                      <div
                        className="ring"
                        style={{ "--value": `${progress * 3.6}deg` }}
                      >
                        <div>
                          <b>
                            {progress}
                            <small>%</small>
                          </b>
                          <span>materi dibaca</span>
                        </div>
                      </div>
                      <p>
                        {read} dari {total} materi
                      </p>
                      <div className="progress-foot">
                        <span>
                          <i className="blue-dot" />
                          Tes awal
                        </span>
                        <b>
                          {baselineScore === null
                            ? "Belum mulai"
                            : `${baselineScore}%`}
                        </b>
                      </div>
                      <button
                        className="text-btn"
                        onClick={() => navigate("progress")}
                      >
                        Lihat progres
                        <ArrowUpRight size={16} />
                      </button>
                    </section>
                  </div>
                  <div className="learning-stages">
                    {[
                      ["01", "Tes awal", !!record.baseline],
                      ["02", "Pelajari SOP", read === total],
                      ["03", "Tes akhir", !!last],
                      ["04", "Praktik mentor", false],
                    ].map(([n, title, done]) => (
                      <div key={n}>
                        <span className={done ? "done" : ""}>
                          {done ? <Check size={15} /> : n}
                        </span>
                        <b>{title}</b>
                        {n === "04" && <small>verifikasi langsung</small>}
                      </div>
                    ))}
                  </div>
                  <section>
                    <div className="section-head">
                      <div>
                        <span className="eyebrow">
                          JALUR {deck.role.toUpperCase()}
                        </span>
                        <h2>Pelajaran untuk peranmu</h2>
                      </div>
                      <button
                        className="text-btn"
                        onClick={() => navigate("library")}
                      >
                        Semua materi
                        <ArrowUpRight size={16} />
                      </button>
                    </div>
                    <div className="module-grid">
                      {deck.modules.map((m, i) => {
                        const Icon = icons[i],
                          available = m.slides.filter(n => !deck.lessons[n - 1].held),
                          done = available.filter((n) =>
                            record.read.includes(`${role}:${n}`),
                          ).length,
                          mins = available.reduce(
                            (s, n) => s + deck.lessons[n - 1].minutes,
                            0,
                          );
                        return (
                          <button
                            key={m.id}
                            className={`module-card tone-${i}`}
                            disabled={!available.length}
                            onClick={() => openModule(i)}
                          >
                            <div className="row-between">
                              <span className="module-icon">
                                <Icon size={25} />
                              </span>
                              <span className="module-number">0{i + 1}</span>
                            </div>
                            <h3>{m.title}</h3>
                            <p>{m.description}</p>
                            <div className="module-meta">
                              <span>{available.length} materi{m.slides.length > available.length ? ` · ${m.slides.length - available.length} ditinjau` : ""}</span>
                              <span>±{mins} menit baca</span>
                            </div>
                            <Progress
                              value={pct(done, available.length)}
                              label={`Progres ${m.title}`}
                            />
                            <div className="row-between module-bottom">
                              <span>
                                {done
                                  ? `${done}/${available.length} sudah dibaca`
                                  : "Belum dimulai"}
                              </span>
                              <ArrowRight size={18} />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                  <div className="bottom-grid">
                    <section className="practice-card">
                      <span className="icon-tile lilac">
                        <MessageCircle />
                      </span>
                      <div>
                        <span className="eyebrow">
                          DARI TEORI KE SITUASI NYATA
                        </span>
                        <h3>Kalau ini terjadi di tokomu?</h3>
                        <p>Coba memilih respons dan pelajari alasannya.</p>
                      </div>
                      <button
                        className="secondary"
                        onClick={() => setPractice("scenario")}
                      >
                        Coba simulasi
                        <ArrowRight size={16} />
                      </button>
                    </section>
                    <section className="small-card">
                      <span className="icon-tile mint">
                        <ClipboardCheck />
                      </span>
                      <div>
                        <h3>Susun alur transaksi</h3>
                        <p>Lima langkah. Urutan yang tepat.</p>
                        <button
                          className="text-btn"
                          onClick={() => setPractice("order")}
                        >
                          Mulai latihan
                          <ArrowRight size={16} />
                        </button>
                      </div>
                    </section>
                  </div>
                  {!record.baseline && (
                    <p className="quiet">
                      Materi tetap bisa dibuka. Untuk mengukur peningkatan
                      dengan lebih baik, kerjakan tes awal sebelum belajar.
                    </p>
                  )}
                </>
              ) : page === "library" ? (
                <>
                  <div className="page-intro">
                    <div>
                      <span className="eyebrow">
                        RUJUKAN SAAT KAMU MEMBUTUHKAN
                      </span>
                      <h1>
                        Pustaka SOP<span className="title-dot">.</span>
                      </h1>
                      <p>
                        {deck.lessons.length} slide panduan {deck.role}{documents.length ? ` dan ${documents.length} dokumen untuk pengelola` : ""}. Materi yang ditahan menunggu review ditandai di pustaka.
                      </p>
                    </div>
                  </div>
                  <div className="library-tools">
                    <div className="segmented">
                      {[
                        ["slides", "Panduan jabatan"],
                        ...(documents.length ? [["sop", "Dokumen SOP"]] : []),
                        ["saved", "Tersimpan"],
                      ].map(([id, title]) => (
                        <button
                          key={id}
                          className={libraryTab === id ? "active" : ""}
                          onClick={() => setLibraryTab(id)}
                        >
                          {title}
                        </button>
                      ))}
                    </div>
                    <label className="search">
                      <Search size={18} />
                      <input
                        aria-label="Cari materi atau SOP"
                        placeholder="Cari topik, mis. garansi…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                      {query && (
                        <button
                          className="icon-btn"
                          onClick={() => setQuery("")}
                          aria-label="Hapus pencarian"
                        >
                          <X size={15} />
                        </button>
                      )}
                    </label>
                  </div>
                  {(() => {
                    const all =
                        libraryTab === "sop"
                          ? documents
                          : libraryTab === "saved"
                            ? decks
                                .flatMap((d) => d.lessons)
                                .filter((l) => record.bookmarks.includes(l.id))
                            : deck.lessons,
                      items = all.filter((i) =>
                        (libraryTab === "sop"
                          ? i.title + " " + i.text
                          : i.title +
                            " " +
                            i.subtitle +
                            " " +
                            i.blocks.flat().join(" ")
                        )
                          .toLowerCase()
                          .includes(query.toLowerCase()),
                      );
                    return (
                      <>
                        <p className="result-count">
                          {items.length} hasil{" "}
                          {libraryTab === "sop"
                            ? "dokumen"
                            : `untuk ${deck.role}`}
                        </p>
                        <div className="library-list">
                          {items.slice(0, visibleCount).map((i) => (
                            <button
                              className="library-row"
                              key={i.id}
                              onClick={() =>
                                libraryTab === "sop"
                                  ? setDocumentId(i.id)
                                  : setLessonId(i.id)
                              }
                            >
                              <span className="icon-tile pale">
                                {libraryTab === "sop" ? (
                                  <FileText size={21} />
                                ) : (
                                  <BookOpen size={21} />
                                )}
                              </span>
                              <div>
                                <h3>{i.title}</h3>
                                <p>
                                  {libraryTab === "sop"
                                    ? i.file
                                    : `Slide ${i.number} · ${decks.find((d) => d.id === i.id.split(":")[0]).role} · ${i.minutes} menit baca`}
                                </p>
                                {i.flags?.length > 0 && (
                                  <span className="inline-review">
                                    Ada catatan materi
                                  </span>
                                )}
                              </div>
                              {record.read.includes(i.id) && (
                                <CheckCircle2 className="green" size={20} />
                              )}
                              <ArrowUpRight size={18} />
                            </button>
                          ))}
                        </div>
                        {!items.length && (
                          <div className="empty-state">
                            <Search size={30} />
                            <h3>
                              {libraryTab === "saved"
                                ? "Belum ada materi tersimpan"
                                : "Topik belum ditemukan"}
                            </h3>
                            <p>
                              {libraryTab === "saved"
                                ? "Gunakan ikon bookmark saat membaca materi."
                                : "Coba kata kunci lain pada jalur belajarmu."}
                            </p>
                          </div>
                        )}
                        {items.length > visibleCount && (
                          <button
                            className="secondary load-more"
                            onClick={() => setVisibleCount((n) => n + 12)}
                          >
                            Tampilkan lebih banyak
                          </button>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : page === "progress" ? (
                <>
                  <div className="page-intro">
                    <div>
                      <span className="eyebrow">PAHAMI PERKEMBANGANMU</span>
                      <h1>
                        Setiap langkah tercatat
                        <span className="title-dot">.</span>
                      </h1>
                      <p>
                        {deck.role} · progres belajar {account.name}
                      </p>
                    </div>
                    <button className="secondary" onClick={download}>
                      <Download size={16} />
                      Ekspor hasil
                    </button>
                  </div>
                  {result && (
                    <section className="result-banner" role="status">
                      <span className="result-icon">
                        <CheckCircle2 size={27} />
                      </span>
                      <div>
                        <h2>
                          {result.mode === "pre"
                            ? "Titik awalmu sudah tercatat."
                            : "Tes akhir selesai. Lihat perkembanganmu."}
                        </h2>
                        <p>
                          {result.mode === "pre"
                            ? "Skor ini menjadi pembanding tetap. Mulai dari topik yang masih perlu diperkuat."
                            : "Nilai teori membantu menentukan latihan berikutnya. Praktik tetap perlu dinilai mentor."}
                        </p>
                      </div>
                      <b>
                        {score(result.data)}
                        <small>%</small>
                      </b>
                    </section>
                  )}
                  <div className="metric-grid">
                    {[
                      [
                        "Tes awal",
                        baselineScore === null ? "—" : `${baselineScore}%`,
                        "Hasil pertama tetap tersimpan",
                      ],
                      [
                        "Tes akhir terbaru",
                        lastScore === null ? "—" : `${lastScore}%`,
                        `${record.attempts.length} percobaan tes akhir`,
                      ],
                      [
                        "Perubahan",
                        lastScore === null || baselineScore === null
                          ? "—"
                          : `${lastScore - baselineScore > 0 ? "+" : ""}${lastScore - baselineScore} poin`,
                        "Selisih skor, bukan nilai karyawan",
                      ],
                      [
                        "Materi dibaca",
                        `${read}/${total}`,
                        "Tandai setelah kamu membaca",
                      ],
                    ].map(([title, value, caption]) => (
                      <div className="metric" key={title}>
                        <span>{title}</span>
                        <b>{value}</b>
                        <small>{caption}</small>
                      </div>
                    ))}
                  </div>
                  <div className="progress-columns">
                    <section className="panel">
                      <div className="section-head">
                        <h2>Jawaban per topik</h2>
                        <Target size={20} />
                      </div>
                      {!record.baseline ? (
                        <div className="empty-state">
                          <Target size={28} />
                          <h3>Mulai dengan tes awal</h3>
                          <p>Kenali pemahamanmu sebelum memulai pelajaran.</p>
                          <button
                            className="primary"
                            onClick={() => start("pre")}
                          >
                            Mulai tes awal
                            <ArrowRight size={17} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="topic-list">
                            {questionsFor(role).map((q) => {
                              const b = record.baseline.answers.find(
                                  (a) => a.id === q.id,
                                ),
                                a =
                                  last?.answers.find((a) => a.id === q.id) || b;
                              return (
                                <button
                                  key={q.id}
                                  onClick={() => setLessonId(q.ref)}
                                >
                                  <span
                                    className={`topic-status ${a?.correct ? "ok" : ""}`}
                                  >
                                    {a?.correct ? (
                                      <Check size={15} />
                                    ) : (
                                      <BookOpen size={15} />
                                    )}
                                  </span>
                                  <div>
                                    <b>{q.topic}</b>
                                    <small>
                                      {a?.correct
                                        ? a.confidence === "ragu"
                                          ? "Jawaban tepat · masih ragu"
                                          : "Jawaban tepat"
                                        : "Perlu penguatan"}{" "}
                                      · buka rujukan
                                    </small>
                                  </div>
                                  <ArrowUpRight size={16} />
                                </button>
                              );
                            })}
                          </div>
                          <button
                            className="primary full"
                            onClick={() => start("post")}
                          >
                            {last
                              ? "Coba tes akhir lagi"
                              : "Kerjakan tes akhir"}
                            <ArrowRight size={17} />
                          </button>
                          <p className="quiet">
                            Soal akhir memakai situasi berbeda. Nilai awal tidak
                            ditimpa.
                          </p>
                        </>
                      )}
                    </section>
                    <section className="panel">
                      <span className="eyebrow">LANGKAH BERIKUTNYA</span>
                      <h2>
                        {weak.length
                          ? "Perkuat yang belum yakin"
                          : "Teruskan dari sini"}
                      </h2>
                      <p>
                        Materi prioritas berdasarkan jawaban terakhir, dengan
                        rujukan ke slide.
                      </p>
                      <button
                        className="recommendation"
                        onClick={() => setLessonId(recommend)}
                      >
                        <BookOpen />
                        <div>
                          <b>{getLesson(recommend).title}</b>
                          <small>
                            {getLesson(recommend).minutes} menit baca
                          </small>
                        </div>
                        <ArrowRight size={18} />
                      </button>
                      <div className="practice-progress">
                        <h3>Latihan interaktif</h3>
                        {[
                          ["scenario", "Situasi kerja"],
                          ["order", "Urutan transaksi"],
                        ].map(([id, label]) => (
                          <button key={id} onClick={() => setPractice(id)}>
                            {record.practice[id] ? (
                              <CheckCircle2 className="green" size={19} />
                            ) : (
                              <Play size={19} />
                            )}
                            <span>{label}</span>
                            <small>
                              {record.practice[id] ? "Selesai" : "Coba"}
                            </small>
                          </button>
                        ))}
                      </div>
                      <details className="attempts">
                        <summary>
                          Riwayat tes (
                          {record.attempts.length + (record.baseline ? 1 : 0)})
                        </summary>
                        {[
                          ...(record.baseline
                            ? [{ ...record.baseline, type: "Tes awal" }]
                            : []),
                          ...record.attempts.map((a, i) => ({
                            ...a,
                            type: `Tes akhir ${i + 1}`,
                          })),
                        ].map((a, i) => (
                          <p key={i}>
                            <b>
                              {a.type} · {score(a)}%
                            </b>
                            <small>{dateLabel(a.at)} WIB</small>
                          </p>
                        ))}
                      </details>
                    </section>
                  </div>
                  <section className="mentor-panel">
                    <div className="mentor-intro"><span className="icon-tile lilac"><Users/></span><div><span className="eyebrow">CATATAN MENTOR</span><h2>Terbukti saat dipraktikkan</h2><p>Pengelola menilai praktik kerja. Hasil teori tidak otomatis menerbitkan sertifikat atau menambah wewenang.</p></div><span className="badge amber">{record.mentor ? record.mentor.outcome === "siap_praktik" ? "Siap praktik" : "Perlu latihan" : "Belum dinilai"}</span></div>
                    <div className="mentor-checks">{practicalChecks.map((text,i)=><label key={text}><input type="checkbox" checked={!!record.mentor?.checks[i]} disabled/><span>{text}</span></label>)}</div>
                    {record.mentor && <><p className="learning-note">{record.mentor.note || "Tanpa catatan tambahan."}</p><p className="quiet">{record.mentor.reviewer_name} · {dateLabel(record.mentor.at)} WIB</p></>}
                  </section>
                </>
              ) : (
                <>
                  <div className="page-intro">
                    <div>
                      <span className="eyebrow">RUANG REVIEW PEMILIK</span>
                      <h1>
                        Materi yang perlu diselaraskan
                        <span className="title-dot">.</span>
                      </h1>
                      <p>
                        Isi sumber tetap dipertahankan. Bagian berikut perlu
                        ditinjau sebelum modul menjadi pelatihan resmi.
                      </p>
                    </div>
                  </div>
                  <div className="review-intro">
                    <AlertCircle />
                    <div>
                      <b>{reviewNotes.length} catatan untuk ditinjau</b>
                      <p>
                        Soal contoh berfokus pada prinsip kerja yang jelas.
                        Rumus, tarif, bonus, dan instruksi teknis yang belum
                        selaras tidak dijadikan kunci penilaian.
                      </p>
                    </div>
                  </div>
                  <div className="review-grid">
                    {reviewNotes.map((n, i) => (
                      <article className="review-card" key={n.id}>
                        <div className="row-between">
                          <span className="review-number">0{i + 1}</span>
                          <span className="badge amber">Perlu review</span>
                        </div>
                        <h2>{n.title}</h2>
                        <p>{n.reason}</p>
                        <div className="source-line">
                          <FileText size={15} />
                          {n.source}
                        </div>
                      </article>
                    ))}
                  </div>
                  <div className="source-summary">
                    <Library size={25} />
                    <div>
                      <h3>6 PPT · 136 slide · 32 dokumen pendamping</h3>
                      <p>
                        Versi sumber: September 2026. Setiap pelajaran dan soal
                        menyimpan rujukan slide. Ruang Belajar tidak menerbitkan
                        SOP atau mengubah aturan aplikasi operasional.
                      </p>
                    </div>
                    <button
                      className="secondary"
                      onClick={() => navigate("library")}
                    >
                      Buka pustaka
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
          <footer className="page-footer"><span>{brand} · Belajar, lalu praktikkan.</span><span>Progres tersimpan di akunmu</span></footer>
      </div>
      {lesson && (
        <Dialog wide title={lesson.title} onClose={() => openLesson(null)}>
          <div className="reader-meta">
            <span className="badge blue">
              {decks.find((d) => d.id === lesson.id.split(":")[0]).role}
            </span>
            <span>
              Slide {lesson.number} · {lesson.minutes} menit baca
            </span>
            <button
              className={`icon-btn ${record.bookmarks.includes(lesson.id) ? "bookmarked" : ""}`}
              aria-label={
                record.bookmarks.includes(lesson.id)
                  ? "Hapus bookmark"
                  : "Simpan bookmark"
              }
              onClick={() => toggleBookmark(lesson.id)}
            >
              <Bookmark size={20} />
            </button>
          </div>
          <div className="reader-body">
            <p className="reader-subtitle">
              {lesson.number === 2 ? "Ketuk topik untuk membuka materi panduan." : lesson.subtitle}
            </p>
            {lesson.flags.map((id) => (
              <aside key={id} className="reader-warning">
                <Flag size={18} />
                <div>
                  <b>{reviewNotes.find((n) => n.id === id).title}</b>
                  <p>{reviewNotes.find((n) => n.id === id).reason}</p>
                </div>
              </aside>
            ))}
            <SlideContent key={lesson.id} lesson={lesson} onOpen={openLesson} />
            <details className="personal-note">
              <summary>Catatan pribadiku</summary>
              <textarea
                aria-label="Catatan pribadi materi"
                placeholder="Apa yang ingin kamu ingat dari materi ini?"
                value={draftNote}
                maxLength={3000}
                onChange={e => setDraftNote(e.target.value)}
              />
              <button className="secondary" disabled={!dirty || saving} onClick={()=>patchLesson({lesson_id:lesson.id,note:draftNote})}>{saving ? "Menyimpan…" : "Simpan catatan"}</button>
              <small>{dirty ? "Ada perubahan belum disimpan" : "Tersimpan di akunmu"}</small>
                        </details>
            <p className="source-line">
              <FileText size={15} />
              {decks.find((d) => d.id === lesson.id.split(":")[0]).file} · slide{" "}
              {lesson.number}
            </p>
          </div>
          {saveError && <p className="warning" role="alert">{saveError}</p>}<div className="reader-footer">
            <span>
              {record.read.includes(lesson.id) ? (
                <>
                  <CheckCircle2 size={16} />
                  Sudah dibaca
                </>
              ) : (
                "Selesai membaca?"
              )}
            </span>
            <button
              className="primary"
              disabled={saving || lesson.held || dirty}
              onClick={async () => {
                if (!await markRead(lesson.id)) return;
                const list = lesson.id.startsWith(role + ":")
                    ? deck.modules.find((m) => m.slides.includes(lesson.number))
                        ?.slides.filter(n => !getLesson(`${role}:${n}`).held) || []
                    : [],
                  i = list.indexOf(lesson.number),
                  next = i >= 0 ? list[i + 1] : null;
                if (next) setLessonId(`${role}:${next}`);
                else {
                  setLessonId(null);
                  setNotice(
                    "Materi sudah ditandai dibaca. Lanjutkan sesuai ritmemu.",
                  );
                }
              }}
            >
              {lesson.held ? "Menunggu review" : record.read.includes(lesson.id)
                ? "Lanjutkan"
                : "Tandai dibaca & lanjut"}
              <ArrowRight size={16} />
            </button>
          </div>
        </Dialog>
      )}
      {doc && (
        <Dialog wide title={doc.title} onClose={() => setDocumentId(null)}>
          <div className="reader-body">
            <p className="source-line">
              <FileText size={15} />
              {doc.file} · dokumen sumber untuk review pengelola
            </p>
            <Markdown text={doc.text} />
          </div>
        </Dialog>
      )}
      {practice && (
        <Practice
          key={`${practice}-${role}`}
          kind={practice}
          role={role}
          onClose={() => setPractice(null)}
          onSave={setRecord}
        />
      )}
    </div>
  );
}

